import React, {useContext, useEffect, useRef, useState} from 'react';
import * as Builder from "../../../builder";
import {ButtonBar} from "../../../builder";
import {Alert, Button} from "antd";
import {Translate} from "../../../components/util/translation.util";
import {CountryCodeEnum, RegExEnum, TranslationDomainEnum} from "../../../types/enums";
import {observer} from "mobx-react";
import {StoreContext} from "../../../store.context";
import {useNavigate} from "react-router-dom";
import {TextType} from "../../../builder/form/type/TextType";
import {AgencyPrepareAddFormResponse, AgencyPrepareEditFormResponse} from "../../../types/modules/agency/response";
import {EntityType} from "../../../builder/form/type/EntityType";
import {
    Constraint,
    EmailConstraint,
    MaxLengthConstraint,
    MinLengthConstraint,
    RegExpConstraint,
    RequiredConstraint
} from "../../../builder/form/validation/constraints";
import {ChoiceType} from "../../../builder/form/type/ChoiceType";
import {useFormEventListener} from "../../../provider/event/event.context.provider";
import {AgencyAddRequest, AgencyAddRequestData,} from "../../../types/modules/agency/request";
import {EmailType} from "../../../builder/form/type/EmailType";
import {FormatHandler} from "../../../handler/format.handler";
import {BaseAddResponse, BaseEditResponse} from "../../../types/response";

export const Form: React.FC<{
    onCancel?: () => void,
    onSave: (values: any) => Promise<BaseAddResponse|BaseEditResponse>,
    preparedData: AgencyPrepareAddFormResponse | AgencyPrepareEditFormResponse
}> = observer(({onCancel, onSave, preparedData}) => {
    const {appStore: {countries, findAddress}} = useContext(StoreContext);

    const navigate = useNavigate();
    const formReference = useRef() as any;

    //when you need to change form input element visibility while filling the form, we need to import useFormEventListener hook.
    const {onFormChange} = useFormEventListener();

    const [fetching, setFetching] = useState<boolean>(false)
    const [classifiers, setClassifiers] = useState<any>([]);
    const [formErrors, setFormErrors] = useState<any>([]);
    const [addressDisabled, setAddressDisabled] = useState<boolean>(false)

    const [showMonthlyOverviewEmail, setShowMonthlyOverviewEmail] = useState<boolean>(false);

    const [country, setCountry] = useState<string | undefined>();
    const [postalCodeConstraints, setPostalCodeConstraints] = useState<Constraint[]>([RequiredConstraint()]);
    const [phoneConstraints, setPhoneConstraints] = useState<Constraint[]>([RequiredConstraint(), MaxLengthConstraint(20)]);

    const [address, setAddress] = useState<any>();
    const [addressFindLoading, setAddressFindLoading] = useState<boolean>();
    const [addressWarning, setAddressWarning] = useState<string | null>(null);

    const [oldServicer, setOldServicer] = useState<string>();
    const [newServicer, setNewServicer] = useState<string>();
    const [servicerWarning, setServicerWarning] = useState<string | null>(null);

    useEffect(() => {
        setFetching(false);
        //setting form information from prepared data, classifiers and object data
        setClassifiers(preparedData.classifiers);

        //prefilling the form
        if (!preparedData.data) {
            //form validations will be automatically updated in "Form constraint validation" useEffect
            preparedData.data = {
                data: {
                    stopExcasso: false,
                    sendMonthlyOverview: false,
                    address: {
                        country: CountryCodeEnum.NETHERLANDS
                    }
                }
            } as AgencyAddRequestData;
        }

        formReference.current?.setData({...preparedData.data,...preparedData.extraData});
        handleAddress(preparedData.data.data.address, true);
        setOldServicer(preparedData.data.data.servicer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const eventFn = (changedData: any, formData: any) => {
            if (!formData) return;

            setShowMonthlyOverviewEmail(formData?.data?.sendMonthlyOverview);
            setCountry(formData?.data?.address?.country);

            if (changedData?.data?.address) {
                setAddress(formData?.data?.address);
            }

            if (changedData?.data?.servicer) {
                setNewServicer(changedData?.data?.servicer)
            }

            if (changedData?.data?.sendMonthlyOverview && formData?.data?.sendMonthlyOverview) {
                if (formData?.data?.contactData?.email && !formData?.data?.monthlyOverviewEmail) {
                    formReference.current?.setData({data: {monthlyOverviewEmail: formData?.data?.contactData?.email}})
                }
            }

        };

        //event listener for form
        onFormChange(eventFn);

        //manually trigger the form change event with route loader data
        eventFn({}, preparedData.data);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const findProductTypesById = (id: string | undefined) => {
            const foundObject = classifiers.servicers.find((obj: any) => obj.id === id);
            return foundObject ? foundObject.allowedProductTypes : [];
        }

        if (oldServicer && newServicer) {
            let oldProductTypes = findProductTypesById(oldServicer);
            let newProductTypes = findProductTypesById(newServicer);
            let allValuesPresent = oldProductTypes.every((oldType: any) => newProductTypes.some((newType: any) => newType.id === oldType.id));
            if (!allValuesPresent) {
                setServicerWarning('agency.message.servicer_product_mismatch')
            } else { setServicerWarning(null); }
        }
    }, [oldServicer, newServicer, classifiers?.servicers]);

    //Form constraint validation, if country is changed on the UI, we handle country constraints
    useEffect(() => {
        if (country === CountryCodeEnum.NETHERLANDS) {
            setPostalCodeConstraints([
                RequiredConstraint(),
                RegExpConstraint(RegExEnum.DUTCH_POSTAL_CODE_REGEX, 'global.invalid_nl_postal_code'),
            ]);
        } else {
            setPostalCodeConstraints([
                RequiredConstraint(),
                RegExpConstraint(RegExEnum.POSTAL_CODE_REGEX, 'global.invalid_postal_code'),
            ]);
        }
    }, [country]);

    useEffect(() => {
        formReference.current.reValidateFields(['data.address.postalCode']);
    }, [postalCodeConstraints, phoneConstraints]);

    const onSubmit = (values: AgencyAddRequest) => {
        setFetching(true);

        onSave(values)
                .then((data) => {
                    navigate(`/agency/${data.id}/view`);
                })
                .catch(error => {
                    setFormErrors(error.response.data.errors);
                })
                .finally(() => {
                    setFetching(false);
                })
    }

    const handleAddress = (input: any, initial: boolean = false) => {
        if (input?.country === CountryCodeEnum.NETHERLANDS) {

            const postalCodeRegEx = new RegExp(RegExEnum.DUTCH_POSTAL_CODE_REGEX);
            const houseNumberRegEx = new RegExp(RegExEnum.HOUSE_NR_REGEX);

            if (postalCodeRegEx.test(input?.postalCode || "") && houseNumberRegEx.test(input?.houseNumber || "")) {
                setAddressFindLoading(true);
                findAddress(input).then(({data}) => {
                    if (data?.city || data?.street) {
                        if (!(initial && (data?.city !== input.city || data?.street !== input.street))) {
                            formReference.current?.setData({
                                data: {
                                    address: {
                                        city: data.city,
                                        street: data.street
                                    }
                                }
                            });
                        }

                        setAddressDisabled(true);
                        setAddressWarning(null);
                    } else {
                        setAddressDisabled(false);
                        setAddressWarning('global.message.address_not_found')
                    }
                }).catch(() => {
                    setAddressDisabled(false);
                    setAddressWarning('global.message.address_not_found')
                }).finally(() => {
                    setAddressFindLoading(false);
                })
            } else {
                setAddressDisabled(false);
                setAddressWarning(null);
            }
        } else {
            setAddressDisabled(false);
            setAddressWarning(null);
        }
    }

    useEffect(() => {
        handleAddress(address);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [address?.postalCode, address?.houseNumber, address?.houseNumberAdditions, address?.country]);

    return (
            <>
                {addressWarning && (
                    <Alert
                        showIcon
                        closable
                        style={{marginBottom: "8px"}}
                        message={<Translate message={addressWarning}/>}
                        type={`warning`}
                    />
                )}

                {servicerWarning && (
                    <Alert
                        showIcon
                        closable
                        style={{marginBottom: "8px"}}
                        message={<Translate message={servicerWarning} domain={TranslationDomainEnum.COMPANY_ADMINISTRATION}/>}
                        type={`warning`}
                    />
                )}

                <Builder.Form
                        translationDomain={TranslationDomainEnum.COMPANY_ADMINISTRATION}
                        formErrors={formErrors}
                        ref={formReference}
                        items={[
                            {
                                title: <Translate message={`agency.title.company`}
                                                  domain={TranslationDomainEnum.COMPANY_ADMINISTRATION}/>,
                                position: 'left',
                                inputs: [
                                    preparedData.extraData?.id ?
                                            {
                                                name: 'id',
                                                label: 'agency.fields.relationalId',
                                                type: TextType,
                                                disabled: true,
                                            }
                                            : null,
                                    {
                                        name: 'data.name',
                                        label: 'agency.fields.name',
                                        type: TextType,
                                        constraints: [
                                            RequiredConstraint(),
                                            MaxLengthConstraint(120)
                                        ]
                                    },
                                    preparedData.extraData?.id ?
                                            {
                                                name: 'externalId',
                                                label: 'agency.fields.externalId',
                                                type: TextType,
                                                disabled: true,
                                            }
                                            : null,
                                    {
                                        name: 'data.initials',
                                        label: 'agency.fields.initials',
                                        type: TextType,
                                        constraints: [
                                            MaxLengthConstraint(10)
                                        ]
                                    }
                                ]
                            },
                            {
                                title: <Translate message={`agency.title.contact_data`}
                                                  domain={TranslationDomainEnum.COMPANY_ADMINISTRATION}/>,
                                position: 'left',
                                translationDomain: TranslationDomainEnum.COMPANY_ADMINISTRATION,
                                inputs: [
                                    {
                                        name: 'data.contactData.phone',
                                        label: 'companyContactData.fields.phone',
                                        type: TextType,
                                        constraints: phoneConstraints
                                    },
                                    {
                                        name: 'data.contactData.email',
                                        label: 'companyContactData.fields.email',
                                        type: EmailType,
                                        constraints: [
                                            RequiredConstraint(),
                                            MaxLengthConstraint(80)
                                        ]
                                    },
                                ]
                            },
                            {
                                title: <Translate message={`agency.title.address`}
                                                  domain={TranslationDomainEnum.COMPANY_ADMINISTRATION}/>,
                                position: 'left',
                                translationDomain: TranslationDomainEnum.COMPANY_ADMINISTRATION,
                                inputs: [
                                    {
                                        name: 'data.address.country',
                                        label: 'companyAddress.fields.country',
                                        type: EntityType,
                                        choices: countries,
                                        constraints: [
                                            RequiredConstraint(),
                                        ],
                                    },
                                    {
                                        name: 'data.address.postalCode',
                                        label: 'companyAddress.fields.postalCode',
                                        type: TextType,
                                        constraints: postalCodeConstraints
                                    },
                                    {
                                        name: 'data.address.houseNumber',
                                        label: 'companyAddress.fields.houseNumber',
                                        type: TextType,
                                        constraints: [
                                            RequiredConstraint(),
                                            RegExpConstraint(RegExEnum.HOUSE_NR_REGEX, 'global.invalid_house_number'),
                                            MaxLengthConstraint(15)
                                        ]
                                    },
                                    {
                                        name: 'data.address.houseNumberAdditions',
                                        label: 'companyAddress.fields.houseNumberAdditions',
                                        type: TextType,
                                        constraints: [
                                            MaxLengthConstraint(6)
                                        ]
                                    },
                                    {
                                        name: 'data.address.street',
                                        label: 'companyAddress.fields.street',
                                        type: TextType,
                                        disabled: addressDisabled,
                                        constraints: [
                                            RequiredConstraint(),
                                            MaxLengthConstraint(75)
                                        ],
                                        loading: addressFindLoading
                                    },
                                    {
                                        name: 'data.address.city',
                                        label: 'companyAddress.fields.city',
                                        disabled: addressDisabled,
                                        type: TextType,
                                        constraints: [
                                            RequiredConstraint(),
                                            MaxLengthConstraint(75)
                                        ],
                                        loading: addressFindLoading
                                    }
                                ]
                            },
                            {
                                title: <Translate message={`agency.title.reference_numbers`}
                                                  domain={TranslationDomainEnum.COMPANY_ADMINISTRATION}/>,
                                position: 'right',
                                inputs: [
                                    {
                                        name: 'data.tpNumber',
                                        label: 'agency.fields.tpNumber',
                                        type: TextType,
                                        constraints: [
                                            RequiredConstraint(),
                                            MaxLengthConstraint(10)
                                        ]
                                    },
                                    {
                                        name: 'data.wft',
                                        label: 'agency.fields.wft',
                                        type: TextType,
                                        constraints: [
                                            RequiredConstraint(),
                                            MaxLengthConstraint(10)
                                        ]
                                    },
                                    {
                                        name: 'data.chamberOfCommerceNumber',
                                        label: 'agency.fields.chamberOfCommerceNumber',
                                        type: TextType,
                                        constraints: [
                                            RequiredConstraint(),
                                            MaxLengthConstraint(8),
                                            MinLengthConstraint(8),
                                            RegExpConstraint(RegExEnum.NUMBERS_ONLY, 'global.only_numbers'),
                                        ]
                                    },
                                    {
                                        name: 'data.crmId',
                                        label: 'agency.fields.crmId',
                                        type: TextType,
                                        constraints: [
                                            MaxLengthConstraint(20),
                                        ]
                                    },
                                ]
                            },
                            {
                                title: <Translate message={`agency.title.servicer`}
                                                  domain={TranslationDomainEnum.COMPANY_ADMINISTRATION}/>,
                                position: 'right',
                                inputs: [
                                    {
                                        name: 'data.servicer',
                                        label: 'agency.fields.servicer',
                                        type: EntityType,
                                        choices: classifiers.servicers,
                                        constraints: [
                                            RequiredConstraint(),
                                        ]
                                    }
                                ]
                            },
                            {
                                title: <Translate message={`agency.title.contract_information`}
                                                  domain={TranslationDomainEnum.COMPANY_ADMINISTRATION}/>,
                                position: 'right',
                                inputs: [
                                    {
                                        name: 'data.internetBroker',
                                        label: 'agency.fields.internetBroker',
                                        type: ChoiceType,
                                        expanded: true,
                                        choices: [{value: true, name: "global.yes"}, {value: false, name: "global.no"}],
                                        choiceTranslationDomain: TranslationDomainEnum.MESSAGES,
                                        constraints: [
                                            RequiredConstraint(),
                                        ]
                                    },
                                    {
                                        name: 'data.hasAccess',
                                        label: 'agency.fields.hasAccess',
                                        type: ChoiceType,
                                        expanded: true,
                                        choices: [{value: true, name: "global.yes"}, {value: false, name: "global.no"}],
                                        choiceTranslationDomain: TranslationDomainEnum.MESSAGES,
                                        constraints: [
                                            RequiredConstraint(),
                                        ]
                                    },
                                    {
                                        name: 'data.activeContract',
                                        label: 'agency.fields.activeContract',
                                        type: ChoiceType,
                                        expanded: true,
                                        choices: [{value: true, name: "global.yes"}, {value: false, name: "global.no"}],
                                        choiceTranslationDomain: TranslationDomainEnum.MESSAGES,
                                        constraints: [
                                            RequiredConstraint(),
                                        ]
                                    },
                                    {
                                        name: 'data.canCreateTrialPolicy',
                                        label: 'agency.fields.canCreateTrialPolicy',
                                        type: ChoiceType,
                                        expanded: true,
                                        choices: [{value: true, name: "global.yes"}, {value: false, name: "global.no"}],
                                        choiceTranslationDomain: TranslationDomainEnum.MESSAGES,
                                        constraints: [
                                            RequiredConstraint(),
                                        ]
                                    },
                                    {
                                        name: 'data.canCreateAfterSales',
                                        label: 'agency.fields.canCreateAfterSales',
                                        type: ChoiceType,
                                        expanded: true,
                                        choices: [{value: true, name: "global.yes"}, {value: false, name: "global.no"}],
                                        choiceTranslationDomain: TranslationDomainEnum.MESSAGES,
                                        constraints: [
                                            RequiredConstraint(),
                                        ]
                                    },
                                    {
                                        name: 'data.restrictedProductTypes',
                                        label: 'agency.fields.restrictedProductTypes',
                                        type: EntityType,
                                        choices: classifiers.productTypes,
                                        multiple: true,
                                        choiceTranslationDomain: TranslationDomainEnum.MESSAGES
                                    },
                                ]
                            },
                            {
                                title: <Translate message={`agency.title.payment_information`}
                                                  domain={TranslationDomainEnum.COMPANY_ADMINISTRATION}/>,
                                position: 'right',
                                inputs: [
                                    {
                                        name: 'data.bankData.iban',
                                        label: 'companyBankData.fields.iban',
                                        type: TextType,
                                        onInput: (event: any) => {
                                            event.target.value = FormatHandler.formatIban(event.target.value);
                                        },
                                        constraints: [
                                            MaxLengthConstraint(34)
                                        ]
                                    },
                                    {
                                        name: 'data.bankData.accountHolderName',
                                        label: 'companyBankData.fields.accountHolderName',
                                        type: TextType,
                                        constraints: [
                                            MaxLengthConstraint(70)
                                        ]
                                    },
                                    {
                                        name: 'data.stopExcasso',
                                        label: 'agency.fields.stopExcasso',
                                        type: ChoiceType,
                                        choices: [{value: true, name: "global.yes"}, {value: false, name: "global.no"}],
                                        expanded: true,
                                        multiple: false,
                                        choiceTranslationDomain: TranslationDomainEnum.MESSAGES,
                                        constraints: [
                                            RequiredConstraint(),
                                        ]
                                    },
                                    {
                                        name: 'data.sendMonthlyOverview',
                                        label: 'agency.fields.sendMonthlyOverview',
                                        type: ChoiceType,
                                        choices: [{value: true, name: "global.yes"}, {value: false, name: "global.no"}],
                                        expanded: true,
                                        multiple: false,
                                        choiceTranslationDomain: TranslationDomainEnum.MESSAGES,
                                        constraints: [
                                            RequiredConstraint(),
                                        ]
                                    },
                                    showMonthlyOverviewEmail ? {
                                        name: 'data.monthlyOverviewEmail',
                                        label: 'agency.fields.monthlyOverviewEmail',
                                        type: TextType,
                                        constraints: [
                                            RequiredConstraint(),
                                        ]
                                    } : null,
                                ]
                            },
                        ]}
                        onFinish={(values: any) => {
                            onSubmit(values)
                        }}
                        initialValues={[]}
                />

                <ButtonBar>
                    <Button type="primary" loading={fetching} htmlType={`submit`} onClick={() => {
                        formReference.current?.submitForm()
                    }}><Translate message={`global.button.save`}/></Button>

                    <Button type="default" className={`ant-btn-secondary`} onClick={() => {
                        onCancel && onCancel()
                    }}><Translate message={`global.button.back_to_list`}/></Button>
                </ButtonBar>
            </>
    )
})